import type { DateTime } from "luxon";
import { EffectiveEmployeeStatus } from "../../../../utils/models";

/**
 * Pure decision for whether a scheduled (planned) shift should be hidden on `currentDate`.
 *
 * The backend already deletes the in-window occupancy when a status is set, but a status change
 * only refetches the nurse objects (`refreshNursesData`) — it does NOT reload the schedule `weeks`.
 * So the just-deleted shifts linger in client state until a full schedule reload; this derives
 * visibility from the freshly-refreshed status to keep the profile schedule correct immediately.
 *
 * Past cells (before `today`) reflect what actually happened, so they are never hidden.
 * Active is never hidden. Inactive hides all FUTURE cells. On Leave / Suspended hide only cells
 * inside the half-open window `[start, end)` — on/after `start` and strictly before `end`; an absent
 * bound makes that side open-ended.
 */
export const shouldHideScheduledShiftForStatus = (params: {
	status: EffectiveEmployeeStatus;
	currentDate: DateTime;
	today: DateTime;
	start: DateTime | null;
	end: DateTime | null;
}): boolean => {
	const { status, currentDate, today, start, end } = params;
	if (currentDate.startOf("day") < today.startOf("day")) return false;
	if (status === EffectiveEmployeeStatus.Inactive) return true;
	if (status === EffectiveEmployeeStatus.OnLeave || status === EffectiveEmployeeStatus.Suspended) {
		const day = currentDate.startOf("day");
		const afterStart = !start || day >= start.startOf("day");
		const beforeEnd = !end || day < end.startOf("day");
		return afterStart && beforeEnd;
	}
	return false;
};
